import React, { useCallback, type ReactElement } from 'react';

import {
  initialState,
  reducer,
  REGISTER_FIELDSET,
  UPDATE_FIELD_CONFIDENCE,
  type ConfidenceState,
} from './submission-confidence.state';
import { normalizeFormConfidenceScores } from '../../utils/confidence-data.utils';

export interface ProviderState extends ConfidenceState {
  showConfidence: boolean;
  /** True when confidence chips must not be clickable (read-only submission). */
  locked: boolean;
  handlers: {
    setFieldConfidence: (
      fieldSet: string,
      fieldNames: string | string[],
      confidence: number
    ) => void;

    setFieldConfidence100: (
      fieldSet: string,
      fieldNames: string | string[]
    ) => void;

    /**
     * Registers a dynamic question form's fieldset from the panel that renders
     * it, using the form's own 0-100 `confidenceScores`. Needed because the
     * submission-wide sweep resolves its form list from
     * `submissionCoverage.coverageOptions`, which does not match the forms the
     * renewal app renders (those come from the renewal product and product
     * lines) — leaving those groups unregistered, so their header read 0% and
     * confidence clicks were dropped by the reducer.
     */
    registerFieldset: (
      fieldSet: string,
      confidenceScores?: Record<string, number> | null
    ) => void;
  };
}

export const SubmissionConfidenceContext = React.createContext<ProviderState>({
  ...initialState,
  showConfidence: true,
  locked: false,
  handlers: {
    setFieldConfidence: () => {
      // no-op
    },
    setFieldConfidence100: () => {
      // no-op
    },
    registerFieldset: () => {
      // no-op
    },
  },
});

interface Props {
  readonly initialScores: ConfidenceState;
  readonly showConfidence?: boolean;
  readonly locked?: boolean;
  readonly children: ReactElement;
}

export const SubmissionConfidenceProvider = ({
  initialScores,
  showConfidence = true,
  locked = false,
  children,
}: Props) => {
  const [state, dispatch] = React.useReducer(reducer, initialScores);

  React.useEffect(() => {
    dispatch({
      type: 'RESET_STATE',
      payload: initialScores,
    });
  }, [initialScores]);

  const setFieldConfidence = useCallback(
    (fieldSet: string, fieldNames: string | string[], confidence: number) => {
      dispatch({
        type: UPDATE_FIELD_CONFIDENCE,
        payload: { fieldSet, fieldNames, confidence },
      });
    },
    []
  );

  const setFieldConfidence100 = useCallback(
    (fieldSet: string, fieldNames: string | string[]) => {
      setFieldConfidence(fieldSet, fieldNames, 1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const registerFieldset = useCallback(
    (fieldSet: string, confidenceScores?: Record<string, number> | null) => {
      dispatch({
        type: REGISTER_FIELDSET,
        payload: {
          fieldSet,
          scores: normalizeFormConfidenceScores(confidenceScores),
        },
      });
    },
    []
  );

  const nextState: ProviderState = React.useMemo(
    () => ({
      ...state,
      showConfidence,
      locked,
      handlers: {
        setFieldConfidence,
        setFieldConfidence100,
        registerFieldset,
      },
    }),
    [
      state,
      showConfidence,
      locked,
      setFieldConfidence,
      setFieldConfidence100,
      registerFieldset,
    ]
  );

  return (
    <SubmissionConfidenceContext.Provider value={nextState}>
      {children}
    </SubmissionConfidenceContext.Provider>
  );
};
